import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  message,
  Tag,
  Space,
  Typography,
  Modal,
  Input,
  Statistic,
  Row,
  Col,
  Tabs,
  Popconfirm,
  Select,
  Progress,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  LogoutOutlined,
  FileOutlined,
  UserOutlined,
  DownloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  LoadingOutlined,
  MailOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { adminApi, auditApi, fileApi, statsApi, type AuditLogItem, type DownloadProgress, type NotificationRecipient } from '../services/api';
import { formatBeijingDateTime } from '../utils/date';
import './AdminDashboard.css';

const { Header, Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;

interface FileRecord {
  id: number;
  original_filename: string;
  file_size: number;
  uploader: string;
  status: string;
  created_at: string;
  reviewed_at?: string;
  review_comment?: string;
  upload_comment?: string;
  has_source_code?: boolean;
  source_code_detail?: string | null;
  uploader_ip?: string;
  transfer_direction?: string;
}

interface Stats {
  total_files: number;
  pending_files: number;
  approved_files: number;
  rejected_files: number;
  total_users: number;
}

interface UserRecord {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  auth_type: string;
  last_login_at?: string;
}

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileRecord | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [sourceDetailModalVisible, setSourceDetailModalVisible] = useState(false);
  const [sourceDetailText, setSourceDetailText] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [activeTab, setActiveTab] = useState('pending');
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [forwardModalVisible, setForwardModalVisible] = useState(false);
  const [userFiles, setUserFiles] = useState<FileRecord[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [selectedTargetUserIds, setSelectedTargetUserIds] = useState<number[]>([]);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardUserSearchText, setForwardUserSearchText] = useState('');
  const [mainTab, setMainTab] = useState('review');
  const [userSearchText, setUserSearchText] = useState('');
  const [userSortField, setUserSortField] = useState<'username' | 'last_login_at' | 'auth_type'>('username');
  const [userSortOrder, setUserSortOrder] = useState<'asc' | 'desc'>('asc');
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');

  useEffect(() => {
    loadFiles();
    loadStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs(1);
    }
  }, [activeTab]);

  useEffect(() => {
    if (mainTab === 'users') {
      loadUsers();
    }
  }, [mainTab]);

  useEffect(() => {
    if (mainTab === 'notifications') {
      loadRecipients();
    }
  }, [mainTab]);

  // 计算各状态文件数量
  const fileCounts = useMemo(() => {
    return {
      pending: files.filter((f) => f.status === 'pending').length,
      approved: files.filter((f) => f.status === 'approved').length,
      rejected: files.filter((f) => f.status === 'rejected').length,
      all: files.length,
    };
  }, [files]);

  // 过滤和排序用户
  const filteredAndSortedUsers = useMemo(() => {
    let result = [...users];

    // 模糊搜索
    if (userSearchText) {
      const searchLower = userSearchText.toLowerCase();
      result = result.filter(
        (user) =>
          user.username.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    // 排序
    result.sort((a, b) => {
      let compareResult = 0;

      if (userSortField === 'username') {
        compareResult = a.username.localeCompare(b.username);
      } else if (userSortField === 'auth_type') {
        compareResult = a.auth_type.localeCompare(b.auth_type);
      } else if (userSortField === 'last_login_at') {
        const aTime = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
        const bTime = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
        compareResult = aTime - bTime;
      }

      return userSortOrder === 'asc' ? compareResult : -compareResult;
    });

    return result;
  }, [users, userSearchText, userSortField, userSortOrder]);

  // 中转功能中过滤用户
  const filteredForwardUsers = useMemo(() => {
    if (!forwardUserSearchText) {
      return users;
    }
    const searchLower = forwardUserSearchText.toLowerCase();
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
    );
  }, [users, forwardUserSearchText]);

  const loadFiles = async (status?: string) => {
    setLoading(true);
    try {
      // 总是加载全部文件，以便计算各状态的数量
      const allData = await fileApi.list();
      setFiles(allData);
    } catch (error: any) {
      message.error(error.error || '加载文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await statsApi.getStats();
      setStats(data);
    } catch (error: any) {
      console.error('加载统计信息失败', error);
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    setDownloadingFileId(fileId);
    setDownloadProgress(null);
    downloadAbortRef.current = new AbortController();
    try {
      const blob = await fileApi.downloadWithProgress(
        fileId,
        (progress) => {
          setDownloadProgress(progress);
        },
        downloadAbortRef.current
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('文件下载成功');
    } catch (error: any) {
      if (error.code === 'ERR_CANCELED') {
        message.info('下载已取消');
      } else {
        message.error(error.error || '文件下载失败');
      }
    } finally {
      setDownloadingFileId(null);
      setDownloadProgress(null);
      downloadAbortRef.current = null;
    }
  };

  const handleCancelDownload = () => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.abort();
    }
  };

  useEffect(() => {
    return () => {
      if (downloadAbortRef.current) {
        downloadAbortRef.current.abort();
      }
    };
  }, []);

  const loadLogs = async (page: number) => {
    setLogsLoading(true);
    try {
      const res = await auditApi.list(page, 20);
      setLogs(res.items);
      setLogsTotal(res.total);
      setLogsPage(res.page);
    } catch (error: any) {
      message.error(error.error || '加载审计日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await adminApi.listUsers();
      setUsers(data);
    } catch (error: any) {
      message.error(error.error || '加载用户列表失败');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleResetPassword = (user: UserRecord) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordVisible(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      message.error('请输入新密码');
      return;
    }

    if (newPassword.length < 6) {
      message.error('密码至少6位');
      return;
    }

    try {
      await adminApi.resetUserPassword(selectedUser.id, newPassword);
      message.success('密码重置成功');
      setResetPasswordVisible(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error: any) {
      message.error(error.error || '密码重置失败');
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      await fileApi.remove(fileId);
      message.success('文件已删除');
      loadFiles(activeTab === 'all' ? undefined : activeTab);
      loadStats();
    } catch (error: any) {
      message.error(error.error || '删除文件失败');
    }
  };

  const handleForward = async (user: UserRecord) => {
    setSelectedUser(user);
    setForwardModalVisible(true);
    setSelectedFileIds([]);
    setSelectedTargetUserIds([]);
    
    try {
      const data = await adminApi.getUserFiles(user.id);
      setUserFiles(data.files || []);
    } catch (error: any) {
      message.error(error.error || '加载用户文件失败');
      setUserFiles([]);
    }
  };

  const confirmForward = async () => {
    if (selectedFileIds.length === 0) {
      message.error('请选择要转发的文件');
      return;
    }
    
    if (selectedTargetUserIds.length === 0) {
      message.error('请选择目标用户');
      return;
    }

    setForwardLoading(true);
    try {
      await adminApi.forwardFiles(selectedFileIds, selectedTargetUserIds);
      message.success('文件转发成功');
      setForwardModalVisible(false);
      setSelectedFileIds([]);
      setSelectedTargetUserIds([]);
    } catch (error: any) {
      message.error(error.error || '文件转发失败');
    } finally {
      setForwardLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    try {
      await adminApi.deleteUser(userId);
      message.success(`用户 ${username} 已删除`);
      loadUsers();
    } catch (error: any) {
      message.error(error.error || '删除用户失败');
    }
  };

  const handleToggleAdmin = async (record: UserRecord, makeAdmin: boolean) => {
    try {
      await adminApi.setUserAdmin(record.id, makeAdmin);
      message.success(makeAdmin ? `已将 ${record.username} 设为管理员` : `已取消 ${record.username} 的管理员权限`);
      loadUsers();
    } catch (error: any) {
      message.error(error.error || '修改管理员权限失败');
    }
  };

  const loadRecipients = async () => {
    setRecipientsLoading(true);
    try {
      const data = await adminApi.listNotificationRecipients();
      setRecipients(data);
    } catch (error: any) {
      message.error(error.error || '加载通知收件人失败');
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleAddRecipient = async () => {
    const email = newRecipientEmail.trim();
    if (!email) {
      message.error('请输入邮箱地址');
      return;
    }
    try {
      await adminApi.addNotificationRecipient(email);
      message.success('收件人添加成功');
      setNewRecipientEmail('');
      loadRecipients();
    } catch (error: any) {
      message.error(error.error || '添加收件人失败');
    }
  };

  const handleDeleteRecipient = async (recipientId: number, email: string) => {
    try {
      await adminApi.deleteNotificationRecipient(recipientId);
      message.success(`已删除收件人 ${email}`);
      loadRecipients();
    } catch (error: any) {
      message.error(error.error || '删除收件人失败');
    }
  };

  const handleReview = (file: FileRecord) => {
    setSelectedFile(file);
    setReviewComment('');
    setReviewModalVisible(true);
  };

  const showSourceDetail = (detail?: string | null) => {
    setSourceDetailText(detail || '');
    setSourceDetailModalVisible(true);
  };

  const formatSourceDetailForDisplay = (detail: string) => {
    return detail
      .replace(/^zip_hits=/, 'zip_hits=\n')
      .replace(/^keywords=/, 'keywords=\n')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .join('\n');
  };

  const submitReview = async (action: 'approve' | 'reject') => {
    if (!selectedFile) return;

    try {
      await fileApi.review(selectedFile.id, action, reviewComment);
      message.success(`文件已${action === 'approve' ? '通过' : '拒绝'}审核`);
      setReviewModalVisible(false);
      loadFiles();
      loadStats();
    } catch (error: any) {
      message.error(error.error || '审核失败');
    }
  };


  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      pending: { color: 'orange', text: '待审核' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const columns = [
    {
      title: '文件名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 360,
      render: (_: any, record: FileRecord) => (
        <Space direction="vertical" size={0}>
          <span>{record.original_filename}</span>
          {record.upload_comment ? (
            <span style={{ color: '#999' }}>备注: {record.upload_comment}</span>
          ) : null}
          {record.has_source_code ? (
            <Tag color="volcano" style={{ marginTop: 4 }}>
              疑似含源代码
            </Tag>
          ) : null}
          {record.has_source_code && record.source_code_detail ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => showSourceDetail(record.source_code_detail)}
            >
              查看命中详情
            </Button>
          ) : null}
        </Space>
      ),
    },
    {
      title: '上传者',
      dataIndex: 'uploader',
      key: 'uploader',
      width: 120,
    },
    {
      title: '传输方向',
      dataIndex: 'transfer_direction',
      key: 'transfer_direction',
      width: 220,
      render: (v: string | undefined) => v || '公网 -> 内网',
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '上传时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatBeijingDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right' as const,
      render: (_: any, record: FileRecord) => (
        <Space className="file-actions" wrap size={[8, 4]}>
          {downloadingFileId === record.id ? (
            <div style={{ minWidth: 170 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LoadingOutlined />
                <span style={{ fontSize: 12, color: '#666' }}>
                  {downloadProgress?.speedText || '准备中...'}
                </span>
                <Button type="link" size="small" danger onClick={handleCancelDownload}>
                  取消
                </Button>
              </div>
              <Progress
                percent={downloadProgress?.percent || 0}
                size="small"
                status="active"
                format={(p) => `${p}%`}
              />
            </div>
          ) : (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.id, record.original_filename)}
            >
              下载
            </Button>
          )}
          <Popconfirm
            title="确认删除该文件吗？"
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleDeleteFile(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleReview(record)}
              >
                审核
              </Button>
            </>
          )}
          {record.review_comment && (
            <span style={{ color: '#999' }}>备注: {record.review_comment}</span>
          )}
        </Space>
      ),
    },
  ];

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (v: boolean) => (v ? <Tag color="red">管理员</Tag> : <Tag>普通用户</Tag>),
    },
    {
      title: '认证方式',
      dataIndex: 'auth_type',
      key: 'auth_type',
      render: (v: string) => (
        <Tag color={v === 'local' ? 'blue' : 'orange'}>
          {v === 'local' ? '本地账户' : 'IPA账户'}
        </Tag>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (v: string) => v ? formatBeijingDateTime(v) : <span style={{ color: '#999' }}>从未登录</span>,
    },
    {
      title: '操作',
      key: 'action',
      render: (record: UserRecord) => (
        <Space wrap>
          {record.id !== user?.id && (
            record.is_admin ? (
              <Popconfirm
                title={`确认取消 ${record.username} 的管理员权限吗？`}
                okText="确认"
                cancelText="取消"
                onConfirm={() => handleToggleAdmin(record, false)}
              >
                <Button type="link" danger>
                  取消管理员
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title={`确认将 ${record.username} 设为管理员吗？`}
                okText="确认"
                cancelText="取消"
                onConfirm={() => handleToggleAdmin(record, true)}
              >
                <Button type="link">
                  设为管理员
                </Button>
              </Popconfirm>
            )
          )}
          {record.auth_type === 'local' && record.username !== 'admin' && (
            <>
              <Button
                type="link"
                onClick={() => handleResetPassword(record)}
              >
                重置密码
              </Button>
              <Popconfirm
                title="确认删除该用户吗？"
                description={`删除用户 ${record.username} 会同时删除其所有文件`}
                okText="删除"
                cancelText="取消"
                onConfirm={() => handleDeleteUser(record.id, record.username)}
              >
                <Button type="link" danger>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
          {record.auth_type === 'local' && record.username === 'admin' && (
            <Button
              type="link"
              onClick={() => handleResetPassword(record)}
            >
              重置密码
            </Button>
          )}
          {record.auth_type === 'freeipa' && (
            <Button
              type="link"
              onClick={() => handleForward(record)}
            >
              中转
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const logColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => formatBeijingDateTime(v),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      render: (v: string | undefined) => v || '-',
    },
    {
      title: '提交者',
      dataIndex: 'username',
      key: 'username',
      render: (v: string | undefined) => v || '-',
    },
    {
      title: '文件',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (v: string | undefined) => v || '-',
    },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      render: (v: string) => {
        const map: Record<string, string> = {
          upload: '上传',
          download: '下载',
          delete: '删除',
          review_approve: '审核通过',
          review_reject: '审核拒绝',
          review_approve_auto: '自动通过（公网→内网）',
          review_reject_auto: '自动拒绝（病毒）',
          set_admin: '修改管理员权限',
          add_notification_recipient: '添加通知收件人',
          delete_notification_recipient: '删除通知收件人',
          reset_password: '重置密码',
          delete_user: '删除用户',
        };
        return map[v] || v;
      },
    },
    {
      title: '传输方向',
      dataIndex: 'transfer_direction',
      key: 'transfer_direction',
      render: (v: string | undefined) => v || '-',
    },
    {
      title: '详情',
      dataIndex: 'detail',
      key: 'detail',
      ellipsis: true,
    },
  ];

  return (
    <Layout className="admin-layout">
      <Header className="admin-header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              color: '#ffffff', 
              fontSize: '20px', 
              fontWeight: '700',
              letterSpacing: '1px'
            }}>
              Raina
            </span>
            <Title level={4} style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: '400' }}>
              FTP审核系统 - 管理后台
            </Title>
          </div>
          <Space>
            <Button onClick={() => navigate('/dashboard')}>返回前台</Button>
            <span style={{ color: '#fff' }}>管理员: {user?.username}</span>
            <Button
              icon={<LogoutOutlined />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              退出
            </Button>
          </Space>
        </div>
      </Header>
      <Content className="admin-content">
        <div className="content-container">
          {stats && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card
                  hoverable
                  onClick={() => {
                    setMainTab('files');
                    loadFiles(undefined);
                  }}
                >
                  <Statistic
                    title="总文件数"
                    value={stats.total_files}
                    prefix={<FileOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  onClick={() => {
                    setMainTab('review');
                    setActiveTab('pending');
                    loadFiles('pending');
                  }}
                >
                  <Statistic
                    title="待审核"
                    value={stats.pending_files}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  onClick={() => {
                    setMainTab('review');
                    setActiveTab('approved');
                    loadFiles('approved');
                  }}
                >
                  <Statistic
                    title="已通过"
                    value={stats.approved_files}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  onClick={() => {
                    setMainTab('users');
                    loadUsers();
                  }}
                >
                  <Statistic
                    title="总用户数"
                    value={stats.total_users}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <Card>
            <Tabs
              activeKey={mainTab}
              onChange={(key) => {
                setMainTab(key);
                if (key === 'review') {
                  setActiveTab('pending');
                  loadFiles('pending');
                } else if (key === 'files') {
                  loadFiles(undefined);
                } else if (key === 'users') {
                  loadUsers();
                } else if (key === 'logs') {
                  loadLogs(1);
                }
              }}
              items={[
                {
                  key: 'review',
                  label: '文件审核',
                  children: (
                    <Tabs
                      activeKey={activeTab}
                      onChange={(key) => {
                        setActiveTab(key);
                        loadFiles(key);
                      }}
                      items={[
                        {
                          key: 'pending',
                          label: `待审核 (${fileCounts.pending})`,
                          children: (
                            <Table
                              className="admin-files-table"
                              columns={columns}
                              dataSource={files.filter((f) => f.status === 'pending')}
                              rowKey="id"
                              loading={loading}
                              pagination={{ pageSize: 10 }}
                              scroll={{ x: 'max-content' }}
                            />
                          ),
                        },
                        {
                          key: 'approved',
                          label: `已通过 (${fileCounts.approved})`,
                          children: (
                            <Table
                              className="admin-files-table"
                              columns={columns}
                              dataSource={files.filter((f) => f.status === 'approved')}
                              rowKey="id"
                              loading={loading}
                              pagination={{ pageSize: 10 }}
                              scroll={{ x: 'max-content' }}
                            />
                          ),
                        },
                        {
                          key: 'rejected',
                          label: `已拒绝 (${fileCounts.rejected})`,
                          children: (
                            <Table
                              className="admin-files-table"
                              columns={columns}
                              dataSource={files.filter((f) => f.status === 'rejected')}
                              rowKey="id"
                              loading={loading}
                              pagination={{ pageSize: 10 }}
                              scroll={{ x: 'max-content' }}
                            />
                          ),
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'files',
                  label: `文件列表 (${fileCounts.all})`,
                  children: (
                    <Table
                      className="admin-files-table"
                      columns={columns}
                      dataSource={files}
                      rowKey="id"
                      loading={loading}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 'max-content' }}
                    />
                  ),
                },
                {
                  key: 'users',
                  label: '用户列表',
                  children: (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <Space style={{ marginBottom: 8 }}>
                          <Input
                            placeholder="搜索用户名或邮箱"
                            prefix={<SearchOutlined />}
                            value={userSearchText}
                            onChange={(e) => setUserSearchText(e.target.value)}
                            style={{ width: 260 }}
                            allowClear
                          />
                          <Select
                            value={userSortField}
                            onChange={setUserSortField}
                            style={{ width: 140 }}
                          >
                            <Select.Option value="username">按用户名</Select.Option>
                            <Select.Option value="auth_type">按认证方式</Select.Option>
                            <Select.Option value="last_login_at">按登录时间</Select.Option>
                          </Select>
                          <Select
                            value={userSortOrder}
                            onChange={setUserSortOrder}
                            style={{ width: 120 }}
                          >
                            <Select.Option value="asc">升序</Select.Option>
                            <Select.Option value="desc">降序</Select.Option>
                          </Select>
                        </Space>
                        <div style={{ color: '#999', fontSize: '12px', marginTop: 8 }}>
                          IPA用户每天凌晨2点自动同步
                        </div>
                      </div>
                      <Table
                        columns={userColumns}
                        dataSource={filteredAndSortedUsers}
                        rowKey="id"
                        loading={usersLoading}
                        pagination={{ pageSize: 10 }}
                      />
                    </>
                  ),
                },
                {
                  key: 'notifications',
                  label: '通知设置',
                  children: (
                    <>
                      <div style={{ marginBottom: 16, color: '#666' }}>
                        以下邮箱将接收「新文件待审核」及系统自动审核备案等管理员通知邮件。
                      </div>
                      <Space style={{ marginBottom: 16 }}>
                        <Input
                          placeholder="输入邮箱地址，如 user@raina.tech"
                          prefix={<MailOutlined />}
                          value={newRecipientEmail}
                          onChange={(e) => setNewRecipientEmail(e.target.value)}
                          onPressEnter={handleAddRecipient}
                          style={{ width: 320 }}
                          allowClear
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRecipient}>
                          添加收件人
                        </Button>
                      </Space>
                      <Table
                        columns={[
                          {
                            title: '邮箱',
                            dataIndex: 'email',
                            key: 'email',
                          },
                          {
                            title: '添加时间',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            render: (v: string) => v ? formatBeijingDateTime(v) : '-',
                          },
                          {
                            title: '操作',
                            key: 'action',
                            render: (record: NotificationRecipient) => (
                              <Popconfirm
                                title={`确认删除收件人 ${record.email} 吗？`}
                                okText="删除"
                                cancelText="取消"
                                onConfirm={() => handleDeleteRecipient(record.id, record.email)}
                              >
                                <Button type="link" danger icon={<DeleteOutlined />}>
                                  删除
                                </Button>
                              </Popconfirm>
                            ),
                          },
                        ]}
                        dataSource={recipients}
                        rowKey="id"
                        loading={recipientsLoading}
                        pagination={false}
                      />
                    </>
                  ),
                },
                {
                  key: 'logs',
                  label: '历史日志',
                  children: (
                    <Table
                      columns={logColumns}
                      dataSource={logs}
                      rowKey="id"
                      loading={logsLoading}
                      pagination={{
                        current: logsPage,
                        total: logsTotal,
                        pageSize: 20,
                        onChange: (p) => loadLogs(p),
                      }}
                    />
                  ),
                },
              ]}
            />
          </Card>

          <Modal
            title="疑似源码命中详情"
            open={sourceDetailModalVisible}
            onCancel={() => {
              setSourceDetailModalVisible(false);
              setSourceDetailText('');
            }}
            footer={[
              <Button
                key="close"
                onClick={() => {
                  setSourceDetailModalVisible(false);
                  setSourceDetailText('');
                }}
              >
                关闭
              </Button>,
            ]}
          >
            <div
              style={{
                maxHeight: 360,
                overflowY: 'auto',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {sourceDetailText ? formatSourceDetailForDisplay(sourceDetailText) : '-'}
            </div>
          </Modal>

          <Modal
            title="文件审核"
            open={reviewModalVisible}
            onCancel={() => setReviewModalVisible(false)}
            footer={[
              <Button
                key="reject"
                danger
                icon={<CloseOutlined />}
                onClick={() => submitReview('reject')}
              >
                拒绝
              </Button>,
              <Button
                key="approve"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => submitReview('approve')}
              >
                通过
              </Button>,
            ]}
          >
            <p>
              <strong>文件名:</strong> {selectedFile?.original_filename}
            </p>
            <p>
              <strong>上传者:</strong> {selectedFile?.uploader}
            </p>
            <p>
              <strong>文件大小:</strong>{' '}
              {selectedFile && formatFileSize(selectedFile.file_size)}
            </p>
            <div style={{ marginTop: 16 }}>
              <TextArea
                rows={4}
                placeholder="审核备注（可选）"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </div>
          </Modal>

          <Modal
            title="重置用户密码"
            open={resetPasswordVisible}
            onCancel={() => {
              setResetPasswordVisible(false);
              setNewPassword('');
              setSelectedUser(null);
            }}
            onOk={confirmResetPassword}
            okText="确认重置"
            cancelText="取消"
          >
            {selectedUser && (
              <div>
                <p>
                  <strong>用户名:</strong> {selectedUser.username}
                </p>
                <p>
                  <strong>邮箱:</strong> {selectedUser.email}
                </p>
                <p style={{ marginTop: 16 }}>
                  <strong>新密码:</strong>
                </p>
                <Input.Password
                  placeholder="请输入新密码（至少6位）"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onPressEnter={confirmResetPassword}
                />
              </div>
            )}
          </Modal>

          <Modal
            title={`文件中转 - ${selectedUser?.username}`}
            open={forwardModalVisible}
            onCancel={() => {
              setForwardModalVisible(false);
              setSelectedFileIds([]);
              setSelectedTargetUserIds([]);
              setForwardUserSearchText('');
            }}
            onOk={confirmForward}
            okText="确认转发"
            cancelText="取消"
            confirmLoading={forwardLoading}
            width={800}
          >
            <div>
              <div style={{ marginBottom: 16 }}>
                <strong>选择要转发的文件:</strong>
                <Table
                  columns={[
                    { title: '文件名', dataIndex: 'original_filename', key: 'original_filename' },
                    { title: '状态', dataIndex: 'status', key: 'status', render: (s: string) => (
                      <Tag color={s === 'approved' ? 'green' : s === 'rejected' ? 'red' : 'orange'}>
                        {s === 'approved' ? '已通过' : s === 'rejected' ? '已拒绝' : '待审核'}
                      </Tag>
                    )},
                    { title: '上传时间', dataIndex: 'created_at', key: 'created_at', render: (v: string) => formatBeijingDateTime(v) },
                  ]}
                  dataSource={userFiles}
                  rowKey="id"
                  rowSelection={{
                    selectedRowKeys: selectedFileIds,
                    onChange: (keys) => setSelectedFileIds(keys as number[]),
                  }}
                  pagination={{ pageSize: 5 }}
                  size="small"
                />
              </div>
              
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>选择目标用户:</strong>
                  <Input
                    placeholder="搜索用户名或邮箱"
                    prefix={<SearchOutlined />}
                    value={forwardUserSearchText}
                    onChange={(e) => setForwardUserSearchText(e.target.value)}
                    style={{ width: 240 }}
                    allowClear
                  />
                </div>
                <Table
                  columns={[
                    { title: '用户名', dataIndex: 'username', key: 'username' },
                    { title: '邮箱', dataIndex: 'email', key: 'email' },
                    { title: '认证方式', dataIndex: 'auth_type', key: 'auth_type', render: (v: string) => (
                      <Tag color={v === 'local' ? 'blue' : 'orange'}>
                        {v === 'local' ? '本地' : 'IPA'}
                      </Tag>
                    )},
                  ]}
                  dataSource={filteredForwardUsers.filter(u => u.id !== selectedUser?.id)}
                  rowKey="id"
                  rowSelection={{
                    selectedRowKeys: selectedTargetUserIds,
                    onChange: (keys) => setSelectedTargetUserIds(keys as number[]),
                  }}
                  pagination={{ pageSize: 5 }}
                  size="small"
                />
              </div>
            </div>
          </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default AdminDashboard;
